/**
 * NotchesExplainerFrame — chrome shared by /n2/manual-trading and
 * /n2/automated-trading. Provides the same ivory ground, top brand bar
 * (which links back to /n2), the utility footer, and Fraunces load.
 *
 * The wizard entrance at /n2 has its own inline chrome (see NotchesEntrance)
 * because it composes Level 1/2/3 in one page. These deeper explainer
 * pages need a lighter frame with proper page structure (h1, semantic
 * <main>, breadcrumb back to the wizard).
 */

import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { palette, type as typeTokens } from './theme';

function ensureFraunces() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('n2-fraunces-font')) return;
  const link = document.createElement('link');
  link.id = 'n2-fraunces-font';
  link.rel = 'stylesheet';
  link.href =
    'https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..700;1,9..144,300..700&display=swap';
  document.head.appendChild(link);
}

const REDUCED_MOTION_MEDIA = '(prefers-reduced-motion: reduce)';

export function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCED_MOTION_MEDIA).matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia(REDUCED_MOTION_MEDIA);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return reduced;
}

export function useIsMobile(threshold = 640): boolean {
  const [mobile, setMobile] = useState<boolean>(() =>
    typeof window === 'undefined' ? false : window.innerWidth <= threshold
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onResize = () => setMobile(window.innerWidth <= threshold);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [threshold]);
  return mobile;
}

export const NotchesExplainerFrame: React.FC<{
  breadcrumbTrail: Array<{ label: string; to?: string }>;
  children: React.ReactNode;
}> = ({ breadcrumbTrail, children }) => {
  useEffect(() => {
    ensureFraunces();
  }, []);
  const isMobile = useIsMobile();

  return (
    <div
      className="notches-explainer-root"
      style={{
        minHeight: '100svh',
        background: palette.paper,
        color: palette.ink,
        fontFamily: typeTokens.sans,
        textAlign: 'left',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <TopBrandBar />
      <main
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: isMobile ? '24px 20px 32px' : '40px 32px 48px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 720,
            display: 'flex',
            flexDirection: 'column',
            gap: isMobile ? 28 : 40,
          }}
        >
          <Breadcrumbs trail={breadcrumbTrail} />
          {children}
        </div>
      </main>
      <UtilityFooter />
      <ExplainerKeyframes />
    </div>
  );
};

const TopBrandBar: React.FC = () => (
  <header
    style={{
      display: 'flex',
      alignItems: 'baseline',
      gap: 10,
      padding: '20px 32px 0',
    }}
  >
    <Link
      to="/n2"
      style={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
        textDecoration: 'none',
      }}
    >
      <span
        style={{
          fontFamily: typeTokens.serif,
          fontSize: 22,
          fontWeight: 400,
          color: palette.ink,
          letterSpacing: '-0.015em',
          lineHeight: 1,
        }}
      >
        Notches
      </span>
      <span
        style={{
          fontFamily: typeTokens.sans,
          fontSize: 10,
          fontWeight: 700,
          color: palette.ink3,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
        }}
      >
        by WFS
      </span>
    </Link>
  </header>
);

const Breadcrumbs: React.FC<{
  trail: Array<{ label: string; to?: string }>;
}> = ({ trail }) => (
  <nav
    aria-label="Breadcrumb"
    style={{ fontFamily: typeTokens.sans, fontSize: 13, color: palette.ink3 }}
  >
    <ol
      style={{
        listStyle: 'none',
        padding: 0,
        margin: 0,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
      }}
    >
      {trail.map((crumb, i) => {
        const isLast = i === trail.length - 1;
        return (
          <React.Fragment key={`${crumb.label}-${i}`}>
            {i > 0 && (
              <li aria-hidden="true" style={{ color: palette.ink3 }}>
                /
              </li>
            )}
            <li>
              {isLast || !crumb.to ? (
                <span
                  style={{
                    color: isLast ? palette.ink : palette.ink2,
                    fontWeight: isLast ? 600 : 500,
                  }}
                >
                  {crumb.label}
                </span>
              ) : (
                <Link
                  to={crumb.to}
                  style={{
                    color: palette.ink2,
                    textDecoration: 'underline',
                    textDecorationColor: palette.line,
                    textUnderlineOffset: 3,
                    fontWeight: 500,
                  }}
                >
                  {crumb.label}
                </Link>
              )}
            </li>
          </React.Fragment>
        );
      })}
    </ol>
  </nav>
);

const UtilityFooter: React.FC = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const authRedirect = encodeURIComponent(
    (loc.pathname + loc.search).replace(/^\/+/, '')
  );
  return (
    <footer
      style={{
        padding: '28px 32px 40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        color: palette.ink3,
        fontFamily: typeTokens.sans,
        fontSize: 12,
        borderTop: `1px solid ${palette.line}`,
        marginTop: 24,
      }}
    >
      <div>Built by Wealth Financial Systems</div>
      <nav
        aria-label="Utility"
        style={{
          display: 'flex',
          gap: 18,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <Link to="/n2/about" style={utilityLinkStyle}>
          About
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/n2/methodology" style={utilityLinkStyle}>
          Methodology
        </Link>
        <span aria-hidden="true">·</span>
        {user ? (
          <Link to="/notches/dashboard" style={utilityLinkStyle}>
            Dashboard →
          </Link>
        ) : (
          <Link to={`/login?redirect=${authRedirect}`} style={utilityLinkStyle}>
            Sign in
          </Link>
        )}
      </nav>
    </footer>
  );
};

const utilityLinkStyle: React.CSSProperties = {
  color: palette.ink2,
  textDecoration: 'none',
  borderBottom: `1px solid ${palette.line}`,
  paddingBottom: 1,
  fontWeight: 500,
};

const ExplainerKeyframes: React.FC = () => {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (document.getElementById('notches-explainer-keyframes')) return;
    const style = document.createElement('style');
    style.id = 'notches-explainer-keyframes';
    style.textContent = `
      @keyframes notches-explainer-fade-in {
        from { opacity: 0; transform: translateY(6px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .notches-explainer-root button:focus-visible,
      .notches-explainer-root a:focus-visible {
        outline: 2px solid ${palette.gold};
        outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        .notches-explainer-root * {
          animation: none !important;
          transition: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
};
